import { useEffect, useState } from "react";

import { useNavigate } from "react-router-dom";

import {
  getGlobalAuthChecked,
  setGlobalAuthChecked,
} from "~/components/layout/utils/authState";
import { supabase } from "~/integrations/supabase/client";
import { useProfileStore } from "~/store/profileStore";
import { UserRole } from "~/types/user";

export const useAuthCheck = (path: string) => {
  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const [isLoading, setIsLoading] = useState(!getGlobalAuthChecked());
  const navigate = useNavigate();
  // NOTE: Removed useProfileStore() destructuring here - actions are now retrieved via getState()
  // inside the effect to avoid infinite rerenders (actions in deps cause new refs each render)

  useEffect(() => {
    if (path === "/login") {
      setIsLoading(false);
      return;
    }

    const checkAuth = async () => {
      // Get actions from store state directly to avoid dependency array issues
      const { setProfileData, clearProfile } = useProfileStore.getState();
      if (getGlobalAuthChecked()) {
        const storedRole = localStorage.getItem("userRole") as UserRole;
        if (storedRole) {
          setUserRole(storedRole);
          setIsLoading(false);
          return;
        }
      }

      setIsLoading(true);

      const { data: sessionData, error: sessionError } =
        await supabase.auth.getSession();

      if (sessionError || !sessionData.session) {
        clearProfile();
        navigate("/login");
        return;
      }

      const { data: profileData, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", sessionData.session.user.id)
        .single();

      if (error || !profileData) {
        console.error("Error fetching user profile:", error);
        clearProfile();
        navigate("/login");
        return;
      }

      // Check if user is deactivated
      if (!profileData.is_active) {
        console.log("User account is deactivated, signing out");
        clearProfile();
        await supabase.auth.signOut();
        navigate("/login");
        return;
      }

      setProfileData({
        id: profileData.id,
        name: profileData.name,
        lastname: profileData.lastname,
        email: profileData.email,
        role: profileData.role as UserRole,
      });

      localStorage.setItem("userRole", profileData.role as UserRole);
      setUserRole(profileData.role as UserRole);

      setGlobalAuthChecked(true);
      setIsLoading(false);
    };

    checkAuth();
  }, [navigate, path]); // Removed setProfileData, clearProfile - using getState() inside

  return { userRole, isLoading };
};
